/**
 * sysconfig-adapter.ts — 系统配置域真实 DTO → 原型行形状的接真适配层
 *
 * 真 API 返回规范化 snake_case DTO（SystemLanguageResponse / SystemRegionResponse，
 * 见 @novel-admin/shared system-config.ts）；而原型页（system-config.jsx 的
 * LanguagesTable / RegionsTable）消费的是 mock 的 SystemLanguage / SystemRegion 行
 * （见 ~/data/mock/types.ts），主键字段同名（language_code / region_code /
 * display_name_i18n / enabled / app_selectable），仅时间/操作人字段是 camelCase
 * （updatedAt / updatedBy）+ 语言多一个 lockEnabled。本文件做单向塑形，页面 0 改动。
 *
 * 字段映射要点（DTO → mock 行）：
 *   language_code / region_code        → 同名透传（主键）
 *   display_name_i18n                  → 同名透传（jsonb map）
 *   enabled / app_selectable           → 同名透传
 *   lock_enabled                       → lockEnabled（en 行 true，DB CHECK 锁定）
 *   updated_at(ISO)                    → updatedAt（裁成 "YYYY-MM-DD HH:mm" 对齐原型展示）
 *   default_timezone                   → default_tz（IANA 时区，语言行；regions 无此字段）
 *   （regions 不再有 updated_by / updated_by_name —— dev-docs #823 从 A5 §4/§6 去掉，
 *     toggle 操作员归属改由 audit 事件 update_system_region_enabled 承载，与 languages 同口径）
 */

import type { SystemLanguageResponse, SystemRegionResponse } from '@novel-admin/shared';
import { fmtDateTime } from '~/lib/format';
import type { SystemLanguage, SystemRegion } from '../mock/types';

/** SystemLanguageResponse → 原型 SystemLanguage 行。 */
export function adaptSystemLanguage(dto: SystemLanguageResponse): SystemLanguage {
  return {
    language_code: dto.language_code,
    display_name_i18n: dto.display_name_i18n,
    enabled: dto.enabled,
    app_selectable: dto.app_selectable,
    lockEnabled: dto.lock_enabled,
    updatedAt: fmtDateTime(dto.updated_at),
    // IANA 时区：书籍定时发布按该语言默认时区计算 · 只读 · 由 seed 维护。
    default_tz: dto.default_timezone,
  };
}

export function adaptSystemLanguages(dtos: SystemLanguageResponse[]): SystemLanguage[] {
  return dtos.map(adaptSystemLanguage);
}

/** SystemRegionResponse → 原型 SystemRegion 行。 */
export function adaptSystemRegion(dto: SystemRegionResponse): SystemRegion {
  return {
    region_code: dto.region_code,
    display_name_i18n: dto.display_name_i18n,
    enabled: dto.enabled,
    updatedAt: fmtDateTime(dto.updated_at),
  };
}

export function adaptSystemRegions(dtos: SystemRegionResponse[]): SystemRegion[] {
  return dtos.map(adaptSystemRegion);
}
